<template>
  <!--
    プランのボリュームディスカウント（人数帯ごとの割引率）を入れる小窓。
    請求管理「プランの料金」の行の［人数割設定］から開く。

    **送っているあいだは閉じない**（×・キャンセルとも。B の決まり）。閉じると、あとから届いた
    断りを出す場所がなくなる。:show.sync だと×で親の値がすぐ false になり止められないので、
    :show ＋ @update:show="close" にして close の頭で止める。成功のときだけ、親が守りなしで閉じる。
    背景を押しても閉じない（「プランを追加」の小窓と同じ。入れかけた帯を失わない）。

    親は開くときだけこの部品を作る（v-if）。**開いたときのプランを data に写して覚え**、
    保存の結果はそのプランのものとしてだけ返す。
  -->
  <CModal
    :show="true"
    :title="title"
    color="primary"
    size="lg"
    :close-on-backdrop="false"
    class="plan-volume-discount-modal"
    @update:show="close"
  >
    <p class="volume-discount-lead">該当する人数の帯ごとに割引率を適用し、1人あたりの単価（1円未満切り捨て）から月額ご利用料を計算します。割引後のご利用料が最低月額に満たない場合は最低月額を適用します。初期特別価格の適用期間中、無料プラン、無料試用中の月は適用されません。なお、確定済みの請求は設定を変更しても更新されません。</p>

    <!--
      **適用請求月（どの月の請求からこの組を使うか）。既定は来月**（今の組の月ではない）。
      見出しは「プランの料金」の表と同じ語（M-A）。送るのはその月の1日。先の月も過去の月も選べる
      （2026-09-30。前は「いつから」を日付で選ばせ、先の日付は選べなかった）。

      **帯と試算は、この欄の月に使われる組と料金にする**（2026-09-30 の見直し）。月を変えたら入れ替える。
      前は開いたときに一度だけ決め、月を当月に変えても来月分の組のまま当月分として保存できた。
      帯を直したあとで月を変えたときは、入れ替えてよいかを聞く（いいえなら月を戻す）。
    -->
    <div class="volume-discount-date">
      <label :for="dateId">適用請求月</label>
      <input
        :id="dateId"
        v-model="appliedMonth"
        type="month"
        class="form-control"
        :disabled="busy"
      >
    </div>
    <!--
      **欄のすぐ下に、いつも出す**（点検 2）。月は入れない（AM-1。表の見出しの下と同じ文）。
    -->
    <p class="volume-discount-date-note">{{ note }}</p>

    <VolumeDiscountTierEditor
      v-model="tiers"
      :price-per-user="price.pricePerUser"
      :base-price="price.basePrice"
      :disabled="busy"
    />

    <!-- 断りは小窓の中（押した［保存する］のすぐ上、本文の最後）に出す。 -->
    <ActionResultMessage :text="error" type="danger"/>

    <template #footer>
      <CButton color="secondary" :disabled="busy" @click="close">キャンセル</CButton>
      <CButton color="primary" class="volume-discount-save" :disabled="busy" @click="save">保存する</CButton>
    </template>
  </CModal>
</template>

<script>
import providerBilling from '@/services/providerBilling'
import ActionResultMessage from '@/components/ActionResultMessage.vue'
import VolumeDiscountTierEditor from './VolumeDiscountTierEditor.vue'
import { normalizeTiers, tierProblem, tiersOfMonth } from '@/utils/volumeDiscount'
import {
  APPLIED_MONTH_NOTE,
  EMPTY_MONTH_MESSAGE,
  entryOfMonth,
  monthStartOf
} from '@/utils/billingMonth'
import { safeResponseMessage } from '@/utils/safeResponseMessage'

/*
 * 保存の失敗で、理由が返らないときの文。**小窓用の文は決めていないので、
 * 契約企業一覧のタブの保存の失敗（V31）と同じ文にする。**
 */
const SAVE_FAILED = '人数割を保存できませんでした。時間をおいて再度お試しください。'

/** 帯を比べる形（人数・率を数にして「人数の昇順」に並べた文字列）。入れかけかどうかを見るため。 */
const tiersKey = tiers => JSON.stringify(normalizeTiers(tiers))

/** 一覧の行の月ごとの並びを写す（開いている間に一覧が読み直されても、開いたときの値で動く）。 */
const copyOf = list => (Array.isArray(list) ? list.map(entry => Object.assign({}, entry)) : [])

export default {
  name: 'PlanVolumeDiscountModal',

  components: { ActionResultMessage, VolumeDiscountTierEditor },

  props: {
    /** 開いたときの「プランの料金」の行。 */
    plan: { type: Object, required: true },
    /** 「適用請求月」の既定（YYYY-MM。来月）。 */
    defaultMonth: { type: String, required: true }
  },

  data () {
    /*
     * **開いたときのプランを写して覚える。**一覧が読み直されても（行が作り直されても）、
     * 送る先と「保存しました」のプラン名はこのプランのまま。
     * 月ごとの組と料金（帯と試算の出どころ）も、開いたときのその行の値を使う。
     */
    const snapshot = {
      id: this.plan.id,
      planName: this.plan.planName,
      priceSchedule: copyOf(this.plan.priceSchedule),
      volumeDiscountSchedule: copyOf(this.plan.volumeDiscountSchedule)
    }
    const tiers = tiersOfMonth(snapshot.volumeDiscountSchedule, monthStartOf(this.defaultMonth))
    return {
      snapshot,
      appliedMonth: this.defaultMonth,
      /* 帯を入れた月（その月の保存済みの組から始めた）と、そのときの帯。入れかけかどうかを見る。 */
      loadedMonth: this.defaultMonth,
      loadedTiersKey: tiersKey(tiers),
      tiers,
      busy: false,
      error: '',
      dateId: 'volume-discount-applied-on-' + this.plan.id
    }
  },

  computed: {
    /* 見出し（V05）。 */
    title () {
      return this.snapshot.planName + ' の人数割設定'
    },
    /* 欄の下の説明（AM-1。月は入れない）。 */
    note () {
      return APPLIED_MONTH_NOTE
    },
    /*
     * 帯の単価と試算に使う定価と基本料：**帯を入れた月に使われる料金**（2026-09-30 の見直し）。
     * 前は一覧の今月の料金で計算していたので、来月分の料金が違う行では、来月分の組を
     * 設定しているのに単価と試算が今月の料金で出た。その月に料金が無ければ出さない。
     */
    price () {
      const entry = entryOfMonth(this.snapshot.priceSchedule, monthStartOf(this.loadedMonth))
      return {
        pricePerUser: entry ? entry.pricePerUser : null,
        basePrice: entry ? entry.basePrice : null
      }
    }
  },

  watch: {
    /*
     * **月を変えたら、その月に使われる組へ入れ替える**（2026-09-30 の見直し）。
     * **帯を直したあとは、入れ替えない**（2026-10-08。料金の行と同じ直し）。前は確認を出して、
     * 「はい」で直した帯を捨て、「いいえ」で月を戻していたので、帯を入れてから月を選ぶ順では
     * 設定できなかった。単価と試算に使う料金は、選んだ月のものへ替える（loadedMonth）。
     * 欄が空・読めない値のあいだは入れ替えない（保存は EMPTY_MONTH_MESSAGE で止まる）。
     */
    appliedMonth (value) {
      const monthStart = monthStartOf(value)
      if (!monthStart || value === this.loadedMonth) return
      this.loadedMonth = value
      this.error = ''
      // 直した帯は残す（loadedTiersKey は直す前の帯のまま。次に月を変えても残る）。
      if (tiersKey(this.tiers) !== this.loadedTiersKey) return
      this.tiers = tiersOfMonth(this.snapshot.volumeDiscountSchedule, monthStart)
      this.loadedTiersKey = tiersKey(this.tiers)
    }
  },

  methods: {
    /** ×・キャンセル。**送っているあいだは閉じない。** */
    close () {
      if (this.busy) return
      this.$emit('close')
    },

    async save () {
      if (this.busy) return
      const wrong = tierProblem(this.tiers)
      if (wrong) {
        // 送らずに止める（back も同じ文で断る）。
        this.error = wrong
        return
      }
      /* 送るのは選んだ月の1日。月の欄を空にしたら送らずに止める（黙って来月にしない。AM-14）。 */
      const appliedOn = monthStartOf(this.appliedMonth)
      if (!appliedOn) {
        this.error = EMPTY_MONTH_MESSAGE
        return
      }
      const planId = this.snapshot.id
      const planName = this.snapshot.planName
      const tiers = normalizeTiers(this.tiers)
      this.busy = true
      this.error = ''
      let saved
      try {
        saved = await providerBilling.savePlanVolumeDiscount(planId, appliedOn, tiers)
      } catch (error) {
        this.error = safeResponseMessage(error, SAVE_FAILED)
        this.busy = false
        return
      }
      /*
       * **ここから先は保存が通っている**（submitPlan の saved と同じ考え）。
       * 閉じるのも一覧の読み直しも親がする。読み直しで落ちても、それは読み込みの失敗で、
       * 「保存できませんでした」ではない（この小窓の断りには出さない）。
       * **送った帯と日付も返す。**親はそれでその行の帯をその場で書き換える（点検 8・9）。
       * back が返した後の月（laterAppliedOn）と確定済みの有無（fixedBillingExists）も渡す
       * （親は料金と同じく結果の文を出し分ける。2026-09-30 の見直し）。
       */
      this.busy = false
      const result = saved || {}
      this.$emit('saved', {
        planId,
        planName,
        appliedOn,
        tiers,
        laterAppliedOn: result.laterAppliedOn || null,
        fixedBillingExists: result.fixedBillingExists === true
      })
    }
  }
}
</script>

<style scoped>
.volume-discount-lead {
  font-size: 0.85rem;
  color: #3c4b64;
  line-height: 1.7;
}

.volume-discount-date { max-width: 12rem; }
.volume-discount-date label { display: block; font-weight: 600; font-size: 0.8rem; }

/* 欄の幅（12rem）に閉じ込めると1行が数字ほどになるので、欄の外（すぐ下）に置く。 */
.volume-discount-date-note {
  margin: 0.35rem 0 0;
  font-size: 0.8rem;
  color: #768192;
}
</style>
